import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/YOGA KAREGA INDIA (7).png';
import './Navigation.css';
import { useLanguage } from '../../contexts/LanguageContext';

function Navigation() {
  const { t, language, toggleLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isRegisterDropdownOpen, setIsRegisterDropdownOpen] = useState(false);
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const serviceDropdownRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    const user = localStorage.getItem('user') || localStorage.getItem('adminUser');
    if (token && user) return true;

    const userDataRaw = localStorage.getItem('userData');
    if (!userDataRaw) return false;

    try {
      const userData = JSON.parse(userDataRaw);
      return !!userData?.isLoggedIn;
    } catch {
      return false;
    }
  });
  const dropdownRef = useRef(null);

  const refreshAuthState = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    const user = localStorage.getItem('user') || localStorage.getItem('adminUser');
    if (token && user) {
      setIsLoggedIn(true);
      return;
    }

    const userDataRaw = localStorage.getItem('userData');
    if (!userDataRaw) {
      setIsLoggedIn(false);
      return;
    }

    try {
      const userData = JSON.parse(userDataRaw);
      setIsLoggedIn(!!userData?.isLoggedIn);
    } catch {
      setIsLoggedIn(false);
    }
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
    setIsRegisterDropdownOpen(false);
    setIsServiceDropdownOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsRegisterDropdownOpen(false);
    setIsServiceDropdownOpen(false);
  };

  const toggleServiceDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsServiceDropdownOpen(prev => !prev);
    setIsRegisterDropdownOpen(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    closeMenu();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('userData');
    localStorage.removeItem('currentUserData');
    window.dispatchEvent(new Event('authChanged'));
  };

  const toggleRegisterDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRegisterDropdownOpen(prev => !prev);
  };

  // Scroll effect
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 10);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsRegisterDropdownOpen(false);
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
        setIsServiceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'auto';
    return () => (document.body.style.overflow = 'auto');
  }, [isMenuOpen]);

  useEffect(() => {
    refreshAuthState();
    const onAuthChanged = () => refreshAuthState();
    window.addEventListener('authChanged', onAuthChanged);
    window.addEventListener('storage', onAuthChanged);
    return () => {
      window.removeEventListener('authChanged', onAuthChanged);
      window.removeEventListener('storage', onAuthChanged);
    };
  }, [refreshAuthState]);

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="nav-logo" onClick={closeMenu}>
            <img src={logo} alt="Yoga Karega India" />
          </Link>

          <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <Link to="/" className="nav-link" onClick={closeMenu}>{t('nav.home')}</Link>
            <Link to="/about" className="nav-link" onClick={closeMenu}>{t('nav.about')}</Link>

            {/* Service Dropdown */}
            <div className="nav-dropdown-container" ref={serviceDropdownRef}>
              <button className="nav-link nav-dropdown-toggle" onClick={toggleServiceDropdown}>
                Services <span className={`dropdown-arrow ${isServiceDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              {isServiceDropdownOpen && (
                <div className="nav-dropdown-menu">
                  <Link to="/service/yoga-at-your-home" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>🏠 Yoga at Your Home</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/service/online-yoga" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>💻 Online Yoga</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/service/yoga-studio" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>🧘 Yoga Studio</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/service/corporate-yoga" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>🏢 Corporate Yoga</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/service/pregnancy-yoga" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>🤱 Pregnancy Yoga</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/service/meditation-magic" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>🧠 Meditation Magic</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/service/positive-instruction" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>✨ Positive Instruction</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/service/stress-relief" className="nav-dropdown-item" onClick={() => { closeMenu(); }}>🌿 Stress Relief</Link>
                </div>
              )}
            </div>
            <Link to="/trainers" className="nav-link" onClick={closeMenu}>{t('nav.trainers')}</Link>
            <Link to="/community" className="nav-link" onClick={closeMenu}>Community</Link>
            <Link to="/contact" className="nav-link" onClick={closeMenu}>{t('nav.contact')}</Link>

            {/* Dropdown */}
            <div className="nav-dropdown-container" ref={dropdownRef}>
              <button className="nav-link nav-dropdown-toggle" onClick={toggleRegisterDropdown}>
                {isLoggedIn ? t('nav.logout') : 'Register'} <span className={`dropdown-arrow ${isRegisterDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>

              {isRegisterDropdownOpen && (
                <div className="nav-dropdown-menu">
                  <button className="nav-dropdown-item language-toggle" onClick={toggleLanguage}>
                    🌐 {language === 'en' ? 'Hindi' : 'English'}
                  </button>
                  <div className="dropdown-divider"></div>

                  <Link to="/register" className="nav-dropdown-item" onClick={() => { closeMenu(); setIsRegisterDropdownOpen(false); }}>
                    📚 Teacher Registration
                  </Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/register/partner" className="nav-dropdown-item" onClick={() => { closeMenu(); setIsRegisterDropdownOpen(false); }}>
                    🤝 Partner Registration
                  </Link>

                  <div className="dropdown-divider"></div>
                  {isLoggedIn ? (
                    <Link to="/" className="nav-dropdown-item login-item" onClick={() => { handleLogout(); setIsRegisterDropdownOpen(false); }}>
                      🔐 {t('nav.logout')}
                    </Link>
                  ) : (
                    <Link to="/login" className="nav-dropdown-item login-item" onClick={() => { closeMenu(); setIsRegisterDropdownOpen(false); }}>
                      🔐 {t('nav.login')}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Hamburger */}
          <button
            className={`hamburger ${isMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Overlay */}
      {isMenuOpen && <div className="menu-overlay" onClick={closeMenu}></div>}
    </>
  );
}

export default Navigation;
